'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Lock, User, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import GoogleButton from './GoogleButton';
import EmailVerification from './EmailVerification';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { TRIAL_PENDING_KEY } from '@/hooks/useSubscription';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  agb: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState<string | null>(null);
  const [registeredEmail, setRegisteredEmail] = useState<string>('');
  const [registeredPassword, setRegisteredPassword] = useState<string>('');
  const { signUp, signIn, triggerSubscriptionRefresh, setForcePremium } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const passwordValue = watch('password', '');

  const onSubmit = async (data: RegisterFormData) => {
    setLoading(true);
    try {
      const result = await signUp(data.email, data.password, { fullName: data.name });

      const userId = result?.user?.id;

      // Mark trial as pending in localStorage so useSubscription keeps retrying
      if (userId) {
        try {
          localStorage.setItem(TRIAL_PENDING_KEY, userId);
        } catch {
          // ignore
        }
      }

      // Always call the server-side trial grant API with email + name so user_profiles can be upserted
      if (userId) {
        try {
          // Get the current session access token to use as fallback in grant-trial
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;

          await fetch('/api/grant-trial', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId, email: data.email, fullName: data.name, accessToken }),
          });
        } catch {
          // Non-fatal: DB trigger is the primary mechanism
        }

        // Send 6-digit email verification code
        try {
          await fetch('/api/send-verification-code', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: data.email }),
          });
        } catch {
          // Non-fatal: proceed to verification step anyway
        }

        setRegisteredUserId(userId);
        setRegisteredEmail(data.email);
        setRegisteredPassword(data.password);
        setVerificationStep(true);
        toast.success('📧 Bestätigungscode wurde an deine E-Mail gesendet!');
      } else {
        toast.success('🎉 Konto erstellt! Bitte bestätige deine E-Mail-Adresse.');
        router.push('/');
      }
    } catch (err: any) {
      const msg = err?.message ?? 'Registrierung fehlgeschlagen. Bitte versuche es erneut.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerified = async () => {
    // Step 1: Sign the user in to establish a session
    if (registeredEmail && registeredPassword) {
      try {
        await signIn(registeredEmail, registeredPassword);
      } catch {
        // If auto-sign-in fails, fall through
      }
    }

    // Step 2: Set forcePremium immediately so all premium content unlocks right away
    if (setForcePremium) {
      setForcePremium(true);
    }

    // Step 3: Grant trial as final safety net after email verification
    if (registeredUserId) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        await fetch('/api/grant-trial', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: registeredUserId, email: registeredEmail, accessToken }),
        });
      } catch {
        // Non-fatal
      }
    }

    // Step 4: Trigger subscription refresh now that the session is established
    if (triggerSubscriptionRefresh) {
      triggerSubscriptionRefresh();
    }

    toast.success('🎉 Willkommen! Du erhältst 24h kostenlosen Premium-Zugang!');
    router.push('/');
  };

  const passwordStrength = (pwd: string): { label: string; color: string; width: string } => {
    if (!pwd) return { label: '', color: 'var(--border)', width: '0%' };
    if (pwd.length < 6) return { label: 'Zu kurz', color: '#EF4444', width: '25%' };
    if (pwd.length < 8) return { label: 'Schwach', color: '#F59E0B', width: '50%' };
    if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) return { label: 'Stark', color: '#4ADE80', width: '100%' };
    return { label: 'Mittel', color: '#22D3EE', width: '75%' };
  };

  const strength = passwordStrength(passwordValue);

  return (
    <div className="animate-fade-in">
      {verificationStep && registeredUserId ? (
        <EmailVerification
          email={registeredEmail}
          userId={registeredUserId}
          onVerified={handleVerified}
        />
      ) : (
        <>
          <div className="mb-6">
            <h2 className="font-extrabold text-2xl mb-1">Konto erstellen 🚀</h2>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Kostenlos registrieren und sofort spielen.
            </p>
          </div>

          {/* Google OAuth */}
          {/* Backend integration: Supabase Google OAuth signIn */}
          <GoogleButton label="Mit Google registrieren" />

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
            <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>oder</span>
            <div className="flex-1 h-px" style={{ background: 'var(--border)' }} />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            {/* Name */}
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="reg-name">
                Dein Name
              </label>
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  id="reg-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Max Mustermann"
                  className="w-full pl-9 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.name ? '#EF4444' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => {
                    if (!errors.name) e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
                  }}
                  onBlur={(e) => {
                    if (!errors.name) e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  {...register('name', {
                    required: 'Name ist erforderlich.',
                    minLength: { value: 2, message: 'Name muss mindestens 2 Zeichen haben.' },
                  })}
                />
              </div>
              {errors.name && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="reg-email">
                E-Mail-Adresse
              </label>
              <div className="relative">
                <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  id="reg-email"
                  type="email"
                  autoComplete="email"
                  placeholder="deine@email.de"
                  className="w-full pl-9 pr-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.email ? '#EF4444' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => {
                    if (!errors.email) e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
                  }}
                  onBlur={(e) => {
                    if (!errors.email) e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  {...register('email', {
                    required: 'E-Mail-Adresse ist erforderlich.',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Bitte eine gültige E-Mail-Adresse eingeben.',
                    },
                  })}
                />
              </div>
              {errors.email && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="reg-password">
                Passwort
              </label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  id="reg-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Min. 8 Zeichen"
                  className="w-full pl-9 pr-10 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.password ? '#EF4444' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => {
                    if (!errors.password) e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
                  }}
                  onBlur={(e) => {
                    if (!errors.password) e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  {...register('password', {
                    required: 'Passwort ist erforderlich.',
                    minLength: { value: 8, message: 'Passwort muss mindestens 8 Zeichen haben.' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors hover:bg-white/5"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {/* Strength bar */}
              {passwordValue && (
                <div className="mt-2">
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{ width: strength.width, background: strength.color }}
                    />
                  </div>
                  <p className="text-xs mt-1 font-medium" style={{ color: strength.color }}>
                    {strength.label}
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium mb-1.5" htmlFor="reg-confirm">
                Passwort bestätigen
              </label>
              <div className="relative">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input
                  id="reg-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Passwort wiederholen"
                  className="w-full pl-9 pr-10 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'var(--input)',
                    border: `1px solid ${errors.confirmPassword ? '#EF4444' : 'var(--border)'}`,
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => {
                    if (!errors.confirmPassword) e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,58,237,0.15)';
                  }}
                  onBlur={(e) => {
                    if (!errors.confirmPassword) e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  {...register('confirmPassword', {
                    required: 'Bitte Passwort bestätigen.',
                    validate: (val) => val === passwordValue || 'Passwörter stimmen nicht überein.',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors hover:bg-white/5"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={showConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'}
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* AGB Checkbox */}
            <div>
              <div className="flex items-start gap-2">
                <input
                  id="agb"
                  type="checkbox"
                  className="w-4 h-4 mt-0.5 rounded accent-primary flex-shrink-0"
                  {...register('agb', {
                    required: 'Bitte akzeptiere die AGB und Datenschutzerklärung.',
                  })}
                />
                <label htmlFor="agb" className="text-xs cursor-pointer leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  Ich akzeptiere die{' '}
                  <Link href="/impressum#agb" className="underline hover:opacity-80" style={{ color: 'var(--neon-violet)' }}>
                    AGB
                  </Link>
                  {' '}und die{' '}
                  <Link href="/impressum#datenschutz" className="underline hover:opacity-80" style={{ color: 'var(--neon-violet)' }}>
                    Datenschutzerklärung
                  </Link>
                  . Ich bin mindestens 18 Jahre alt.
                </label>
              </div>
              {errors.agb && (
                <p className="text-xs mt-1.5 font-medium" style={{ color: '#EF4444' }}>
                  {errors.agb.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3 font-bold text-sm rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ minHeight: '46px' }}
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <UserPlus size={16} />
                  Konto erstellen
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm mt-5" style={{ color: 'var(--muted-foreground)' }}>
            Bereits registriert?{' '}
            <button
              onClick={onSwitchToLogin}
              className="font-semibold hover:opacity-80 transition-opacity"
              style={{ color: 'var(--neon-violet)' }}
            >
              Jetzt anmelden
            </button>
          </p>

          <p className="text-center text-xs mt-4" style={{ color: 'var(--muted-foreground)' }}>
            Mit der Registrierung stimmst du der{' '}
            <Link href="/impressum#widerruf" className="underline hover:opacity-80">
              Widerrufsbelehrung
            </Link>{' '}
            zu.
          </p>
        </>
      )}
    </div>
  );
}